/**
 * Constructores de JSON-LD reutilizables.
 *
 * Todas las entidades usan @id canónicos y se relacionan entre sí:
 *   Organization  -> /#organization
 *   WebSite       -> /#website  (publisher -> organization)
 *   Xplora        -> /#xplora   (publisher/creator -> organization)
 *
 * Los datos provienen de `company-facts.ts` (fuente única). No duplicar cifras aquí.
 */

import { SITE_URL, ENTITY_IDS, company, product } from "./company-facts";

export { SITE_URL, ENTITY_IDS };

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ENTITY_IDS.organization,
  name: company.legalName,
  alternateName: [...company.alternateName],
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: company.logo,
  },
  description: company.description,
  sameAs: [...company.sameAs],
  knowsAbout: [...company.knowsAbout],
  areaServed: {
    "@type": "Place",
    name: company.areaServed,
  },
  contactPoint: company.offices.map((o) => ({
    "@type": "ContactPoint",
    telephone: o.telephone,
    contactType: "sales",
    areaServed: o.addressCountry,
    availableLanguage: ["Spanish", "English", "Portuguese"],
  })),
  address: company.offices.map((o) => ({
    "@type": "PostalAddress",
    streetAddress: o.streetAddress,
    addressLocality: o.addressLocality,
    addressCountry: o.addressCountry,
  })),
  makesOffer: {
    "@type": "Offer",
    itemOffered: { "@id": ENTITY_IDS.xplora },
  },
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": ENTITY_IDS.website,
  name: company.legalName,
  url: SITE_URL,
  inLanguage: ["es", "en", "pt"],
  publisher: { "@id": ENTITY_IDS.organization },
  about: { "@id": ENTITY_IDS.xplora },
});

export const softwareApplicationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "@id": ENTITY_IDS.xplora,
  name: product.name,
  applicationCategory: product.category,
  applicationSubCategory: product.subCategory,
  operatingSystem: "Web",
  description: product.description,
  url: `${SITE_URL}/es/plataforma`,
  image: company.logo,
  publisher: { "@id": ENTITY_IDS.organization },
  creator: { "@id": ENTITY_IDS.organization },
  provider: { "@id": ENTITY_IDS.organization },
  areaServed: {
    "@type": "Place",
    name: company.areaServed,
  },
  /** Modalidades de despliegue publicadas. Sin precio: Qualita no publica tarifas. */
  featureList: [...product.deploymentModels],
});

/**
 * WebPage / AboutPage / ContactPage / CollectionPage según la ruta.
 * `url` debe ser la URL canónica autoreferencial de la página.
 */
export const webPageSchema = ({
  type = "WebPage",
  url,
  name,
  description,
  inLanguage = "es",
  breadcrumbId,
  datePublished,
  dateModified,
}: {
  type?: "WebPage" | "AboutPage" | "ContactPage" | "CollectionPage";
  url: string;
  name: string;
  description: string;
  inLanguage?: string;
  breadcrumbId?: string;
  datePublished?: string;
  dateModified?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": type,
  "@id": `${url}#webpage`,
  url,
  name,
  description,
  inLanguage,
  isPartOf: { "@id": ENTITY_IDS.website },
  publisher: { "@id": ENTITY_IDS.organization },
  ...(breadcrumbId ? { breadcrumb: { "@id": breadcrumbId } } : {}),
  ...(datePublished ? { datePublished } : {}),
  ...(dateModified ? { dateModified } : {}),
});

/**
 * FAQPage — usar ÚNICAMENTE cuando las preguntas y respuestas están
 * literalmente visibles en la página. No usar QAPage: las FAQs corporativas
 * no admiten respuestas múltiples de usuarios.
 */
export const faqSchema = (items: { question: string; answer: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((i) => ({
    "@type": "Question",
    name: i.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: i.answer,
    },
  })),
});

export const breadcrumbSchema = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${items[items.length - 1]?.url ?? SITE_URL}#breadcrumb`,
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: item.url,
  })),
});
